import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, Upload, X, Image, Trash2, ZoomIn, Search, Filter, Calendar, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHOTOS_PER_PAGE = 20;

import { markModuleSeen } from '@/lib/moduleBadges';

export default function PhotosPage() {
  // Ouvrir le module efface sa pastille de notifications
  useEffect(() => { markModuleSeen(['photo']); }, []);
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isManager = user?.role === 'manager';
  const [photos, setPhotos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [loadingFullPhoto, setLoadingFullPhoto] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    project_id: '',
    files: []
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPhotos, setTotalPhotos] = useState(0);
  
  // Filters
  const [filterProject, setFilterProject] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const fileInputRef = useRef(null);
  const observerRef = useRef(null);

  const fetchInitialData = async () => {
    try {
      const [photosRes, projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/photos?page=1&limit=${PHOTOS_PER_PAGE}&thumbnails_only=true`),
        axios.get(`${API}/projects/light`),
        axios.get(`${API}/users`)
      ]);
      setPhotos(photosRes.data.photos || []);
      setHasMore(photosRes.data.has_more || false);
      setTotalPhotos(photosRes.data.total || 0);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Fallback for old API format
      try {
        const photosRes = await axios.get(`${API}/photos`);
        if (Array.isArray(photosRes.data)) {
          setPhotos(photosRes.data);
          setHasMore(false);
        }
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMorePhotos = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const response = await axios.get(`${API}/photos?page=${nextPage}&limit=${PHOTOS_PER_PAGE}&thumbnails_only=true`);
      const newPhotos = response.data.photos || [];
      setPhotos(prev => [...prev, ...newPhotos]);
      setPage(nextPage);
      setHasMore(response.data.has_more || false);
    } catch (error) {
      console.error('Failed to load more photos:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Load full resolution photo when clicking on thumbnail
  const handlePhotoClick = async (photo) => {
    setPreviewPhoto(photo);
    
    // If we only have thumbnail, fetch full resolution
    if (photo.thumbnail && photo.photo_data === photo.thumbnail) {
      setLoadingFullPhoto(true);
      try {
        const response = await axios.get(`${API}/photos/${photo.id}`);
        setPreviewPhoto(response.data);
      } catch (error) {
        console.error('Failed to load full photo:', error);
      } finally {
        setLoadingFullPhoto(false);
      }
    }
  };

  // Legacy fetchData for compatibility
  const fetchData = fetchInitialData;

  useEffect(() => {
    fetchInitialData();
     
  }, []);

  // Apply filters
  const filteredPhotos = photos.filter(photo => {
    // Project filter
    if (filterProject !== 'all' && photo.project_id !== filterProject) return false;
    
    // User filter
    if (filterUser !== 'all' && photo.user_id !== filterUser) return false;
    
    // Date from filter
    if (filterDateFrom) {
      const photoDate = new Date(photo.created_at).toISOString().split('T')[0];
      if (photoDate < filterDateFrom) return false;
    }
    
    // Date to filter
    if (filterDateTo) {
      const photoDate = new Date(photo.created_at).toISOString().split('T')[0];
      if (photoDate > filterDateTo) return false;
    }
    
    // Search text filter (in description or project name)
    if (searchText) {
      const search = searchText.toLowerCase();
      const matchDescription = photo.description?.toLowerCase().includes(search);
      const matchProject = photo.project_name?.toLowerCase().includes(search);
      const matchUser = photo.user_name?.toLowerCase().includes(search);
      if (!matchDescription && !matchProject && !matchUser) return false;
    }
    
    return true;
  });

  const clearFilters = () => {
    setFilterProject('all');
    setFilterUser('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setSearchText('');
  };

  const hasActiveFilters = filterProject !== 'all' || filterUser !== 'all' || filterDateFrom || filterDateTo || searchText;

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    
    // Validate all files
    for (const file of selectedFiles) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('selectImage'));
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('imageTooLarge'));
        return;
      }
    }
    
    // Upload immediately after selection (WhatsApp style)
    if (!uploadForm.project_id) {
      toast.error(t('selectProject'));
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('project_id', uploadForm.project_id);
    formData.append('description', ''); // Empty description
    
    // Append all photos
    selectedFiles.forEach(file => {
      formData.append('photos', file);
    });
    
    try {
      const response = await axios.post(`${API}/photos/upload-multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      // response.data is now an array of photos
      setPhotos([...response.data, ...photos]);
      setShowUpload(false);
      setUploadForm({ project_id: '', files: [] });
      const count = response.data.length;
      const message = { 
        fr: `${count} photo(s) ajoutée(s)`, 
        ru: `${count} фото добавлено`, 
        md: `${count} foto adăugate`,
        uk: `${count} фото додано`
      }[language] || `${count} photo(s) ajoutée(s)`;
      toast.success(message);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    const confirmMsg = { 
      fr: 'Voulez-vous vraiment supprimer cette photo ?', 
      ru: 'Вы уверены, что хотите удалить это фото?', 
      md: 'Sigur doriți să ștergeți această foto?',
      uk: 'Ви впевнені, що хочете видалити це фото?'
    }[language] || 'Voulez-vous vraiment supprimer cette photo ?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success(t('photoDeleted'));
      setPhotoToDelete(null);
      setPreviewPhoto(null);
    } catch (error) {
      if (error.response?.status === 403) {
        const msg = { 
          fr: 'Vous ne pouvez supprimer que vos propres photos',
          ru: 'Вы можете удалять только свои фото',
          md: 'Poți șterge doar propriile fotografii',
          uk: 'Ви можете видаляти лише власні фото'
        };
        toast.error(msg[language] || msg.fr);
      } else {
        toast.error(t('error'));
      }
    }
  };

  const locale = { fr: 'fr-FR', ru: 'ru-RU', md: 'ro-RO', uk: 'uk-UA' }[language] || 'fr-FR';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="photos-page" className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="page-header mb-0">{t('photosTitle')}</h1>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowFilters(!showFilters)}
            className={`btn-secondary flex items-center gap-2 ${hasActiveFilters ? 'border-[#0E2147] bg-[#0E2147]/5' : ''}`}
          >
            <Filter className="w-4 h-4" />
            {{ fr: 'Filtres', ru: 'Фильтры', md: 'Filtre', uk: 'Фільтри' }[language] || 'Filtres'}
            {hasActiveFilters && <span className="w-2 h-2 bg-[#0E2147] rounded-full" />}
          </Button>
          <Button
            onClick={() => setShowUpload(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="add-photo-btn"
          >
            <Camera className="w-4 h-4" />
            {t('addPhotoButton')}
          </Button>
        </div>
      </div>
      
      {/* Filters Panel */}
      {showFilters && (
        <div className="card-base p-4 animate-slideUp">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold" style={{ color: 'var(--color-primary)' }}>
              <Filter className="w-4 h-4 inline mr-2" />
              {{ fr: 'Filtres', ru: 'Фильтры', md: 'Filtre', uk: 'Фільтри' }[language] || 'Filtres'}
            </h3>
            {hasActiveFilters && (
              <button 
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-[#0E2147]"
              >
                {{ fr: 'Réinitialiser', ru: 'Сбросить', md: 'Resetare', uk: 'Скинути' }[language] || 'Réinitialiser'}
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <Label className="form-label mb-2">
                <Search className="w-3 h-3 inline mr-1" />
                {{ fr: 'Recherche', ru: 'Поиск', md: 'Căutare', uk: 'Пошук' }[language] || 'Recherche'}
              </Label>
              <Input
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={{ fr: 'Description, projet...', ru: 'Описание, проект...', md: 'Descriere, proiect...', uk: 'Опис, об\'єкт...' }[language] || 'Description, projet...'}
                className="input-base"
              />
            </div>
            
            {/* Project Filter */}
            <div>
              <Label className="form-label mb-2">{t('filterByProject')}</Label>
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger className="input-base">
                  <SelectValue placeholder={t('allProjects')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('allProjects')}</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* User Filter */}
            <div>
              <Label className="form-label mb-2">
                {{ fr: 'Auteur', ru: 'Автор', md: 'Autor', uk: 'Автор' }[language] || 'Auteur'}
              </Label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="input-base">
                  <SelectValue placeholder={{ fr: 'Tous', ru: 'Все', md: 'Toți', uk: 'Всі' }[language] || 'Tous'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{{ fr: 'Tous', ru: 'Все', md: 'Toți', uk: 'Всі' }[language] || 'Tous'}</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="form-label mb-2">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  {{ fr: 'Du', ru: 'От', md: 'De la', uk: 'Від' }[language] || 'Du'}
                </Label>
                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="input-base"
                />
              </div>
              <div>
                <Label className="form-label mb-2">
                  {{ fr: 'Au', ru: 'До', md: 'Până la', uk: 'До' }[language] || 'Au'}
                </Label>
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="input-base"
                />
              </div>
            </div>
          </div>
          
          {/* Results count */}
          <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-gray-600">
              <span className="font-bold" style={{ color: 'var(--color-primary)' }}>{filteredPhotos.length}</span> {t('photo')}
              {hasActiveFilters && ` ({{ fr: 'filtrées', ru: 'отфильтровано', md: 'filtrate', uk: 'відфільтровано' }[language] || 'filtrées'})`}
            </p>
          </div>
        </div>
      )}
      
      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {t('newPhoto')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="form-group">
              <Label className="form-label">{t('projects')} *</Label>
              <Select 
                value={uploadForm.project_id} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, project_id: v })}
              >
                <SelectTrigger className="input-base" data-testid="upload-project-select">
                  <SelectValue placeholder={t('selectProject')} />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="form-group">
              <Label className="form-label">{t('photoRequired')}</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="photo-file-input"
                disabled={!uploadForm.project_id || uploading}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={!uploadForm.project_id || uploading}
                className="w-full h-48 border-2 border-dashed border-slate-300 rounded-md flex flex-col items-center justify-center gap-2 hover:border-amber-500 hover:bg-amber-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="select-photo-btn"
              >
                {uploading ? (
                  <>
                    <div className="spinner w-8 h-8" />
                    <span className="text-slate-500 font-medium">
                      {{ fr: 'Envoi en cours...', ru: 'Загрузка...', md: 'Încărcare...', uk: 'Завантаження...' }[language] || 'Envoi en cours...'}
                    </span>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-slate-400" />
                    <span className="text-slate-500 font-medium">{t('clickToSelect')}</span>
                    <span className="text-xs text-slate-400">
                      {{ fr: 'Les photos s\'enverront automatiquement', ru: 'Фото загрузятся автоматически', md: 'Fotografiile se vor încărca automat', uk: 'Фото завантажаться автоматично' }[language] || 'Les photos s\'enverront automatiquement'}
                    </span>
                  </>
                )}
              </button>
              {!uploadForm.project_id && (
                <p className="text-xs text-amber-600 mt-2">
                  {{ fr: 'Sélectionnez d\'abord un chantier', ru: 'Сначала выберите проект', md: 'Selectați mai întâi un proiect', uk: 'Спочатку виберіть об\'єкт' }[language] || 'Sélectionnez d\'abord un chantier'}
                </p>
              )}
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowUpload(false)}
                variant="outline"
                className="btn-secondary flex-1"
                disabled={uploading}
              >
                {t('cancel')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Photo Preview Dialog */}
      <Dialog open={!!previewPhoto} onOpenChange={() => setPreviewPhoto(null)}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden max-h-[90vh] flex flex-col">
          {previewPhoto && (
            <>
              <div className="relative bg-black">
                {loadingFullPhoto && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
                <img 
                  src={previewPhoto.photo_data} 
                  alt={previewPhoto.description || 'Photo'} 
                  className="w-full max-h-[70vh] object-contain"
                  loading="lazy"
                />
              </div>
              <div className="p-4 bg-white">
                {/* Photo Info */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-semibold text-slate-900">{previewPhoto.project_name}</h3>
                    <p className="text-sm text-slate-500 mt-2">
                      {t('by')} {previewPhoto.user_name} • {new Date(previewPhoto.created_at).toLocaleDateString({ fr: 'fr-FR', ru: 'ru-RU', md: 'ro-RO', uk: 'uk-UA' }[language] || 'fr-FR')}
                    </p>
                  </div>
                  {/* Only manager can delete photos */}
                  {isManager && (
                    <Button
                      onClick={() => setPhotoToDelete(previewPhoto)}
                      variant="destructive"
                      size="sm"
                      className="flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('delete')}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Photos Grid */}
      {filteredPhotos.length === 0 ? (
        <div className="card-base p-12">
          <div className="empty-state">
            <Image className="empty-state-icon" />
            <p className="empty-state-text">{t('noPhotos')}</p>
            <Button
              onClick={() => setShowUpload(true)}
              className="btn-primary mt-4"
            >
              {t('addFirstPhoto')}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="photo-grid">
            {filteredPhotos.map((photo, index) => (
              <div
                key={photo.id}
                className="photo-card animate-slideUp group"
                style={{ animationDelay: `${Math.min(index, 20) * 0.03}s` }}
                onClick={() => handlePhotoClick(photo)}
                data-testid={`photo-${photo.id}`}
              >
                <img 
                  src={photo.thumbnail || photo.photo_data} 
                  alt={photo.description || 'Photo'} 
                  loading="lazy"
                />
                <div className="photo-overlay">
                  <p className="font-semibold truncate">{photo.project_name}</p>
                  <p className="text-xs text-slate-300">
                    {new Date(photo.created_at).toLocaleDateString({ fr: 'fr-FR', ru: 'ru-RU', md: 'ro-RO', uk: 'uk-UA' }[language] || 'fr-FR')}
                  </p>
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <ZoomIn className="w-8 h-8 text-white" />
                </div>
              </div>
          ))}
          </div>
          
          {/* Load More Button */}
          {hasMore && !hasActiveFilters && (
            <div className="flex justify-center mt-6">
              <Button
                onClick={loadMorePhotos}
                disabled={loadingMore}
                className="btn-secondary"
                data-testid="load-more-photos-btn"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {{ fr: 'Chargement...', ru: 'Загрузка...', md: 'Încărcare...', uk: 'Завантаження...' }[language] || 'Chargement...'}
                  </>
                ) : (
                  <>
                    {{ fr: 'Charger plus de photos', ru: 'Загрузить ещё', md: 'Încarcă mai multe', uk: 'Завантажити ще' }[language] || 'Charger plus de photos'}
                    {totalPhotos > 0 && ` (${photos.length}/${totalPhotos})`}
                  </>
                )}
              </Button>
            </div>
          )}
          
          {/* Photos count */}
          <div className="text-center mt-4 text-sm text-gray-500">
            {{ fr: 'Photos affichées', ru: 'Показано фото', md: 'Fotografii afișate', uk: 'Показано фото' }[language] || 'Photos affichées'}: {filteredPhotos.length}
            {totalPhotos > 0 && ` / ${totalPhotos}`}
          </div>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!photoToDelete} onOpenChange={() => setPhotoToDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              {{ fr: 'Supprimer la photo ?', ru: 'Удалить фото?', md: 'Ștergi fotografia?', uk: 'Видалити фото?' }[language] || 'Supprimer la photo ?'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {photoToDelete && (
              <>
                <img 
                  src={photoToDelete.photo_data} 
                  alt="Preview" 
                  className="w-full h-48 object-cover rounded-md"
                />
                <p className="text-slate-600 text-sm">
                  {{ fr: 'Êtes-vous sûr de vouloir supprimer cette photo ? Cette action est irréversible.', ru: 'Вы уверены, что хотите удалить это фото? Это действие необратимо.', md: 'Sigur ștergi această fotografie? Această acțiune este ireversibilă.', uk: 'Ви впевнені, що хочете видалити це фото? Ця дія незворотна.' }[language] || 'Êtes-vous sûr de vouloir supprimer cette photo ? Cette action est irréversible.'}
                </p>
              </>
            )}
            <div className="flex gap-3 justify-end">
              <Button
                onClick={() => setPhotoToDelete(null)}
                variant="outline"
                className="btn-secondary"
              >
                {t('cancel')}
              </Button>
              <Button
                onClick={() => photoToDelete && handleDelete(photoToDelete.id)}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {{ fr: 'Supprimer', ru: 'Удалить', md: 'Șterge', uk: 'Видалити' }[language] || 'Supprimer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
